import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  X,
  Save,
  Camera,
  ImagePlus,
  ChevronLeft,
  ChevronRight,
  MapPin,
  RotateCw,
  RotateCcw,
  Sparkles,
  Loader2,
  Trash2,
  Crop,
  Undo2,
} from 'lucide-react';
import { api } from '../../services/api';
import {
  ITEM_CATEGORIES,
  ITEM_CONDITIONS,
  ITEM_CONDITION_LABELS,
  type ItemCategory,
  type ItemCondition,
  type UpdateItemInput,
} from '@findmystuff/shared';
import clsx from 'clsx';
import { ImageLightbox } from './ImageLightbox';
import { PaywallModal } from './PaywallModal';
import { GpsMap } from './GpsMap';
import { PhraseDropdown } from './PhraseDropdown';
import { useAiCredits } from '../../hooks/useAiCredits';

interface LocationImageData {
  data: string;
  mimeType: string;
  preview: string;
}

interface ItemEditModalProps {
  itemId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function ItemEditModal({ itemId, isOpen, onClose }: ItemEditModalProps) {
  const queryClient = useQueryClient();

  const [editData, setEditData] = useState<UpdateItemInput>({});
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [locationImageData, setLocationImageData] = useState<LocationImageData | null>(null);
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  // Transfer state
  const [transferInventoryId, setTransferInventoryId] = useState<string>('');

  // Saved locations state
  const [locationMode, setLocationMode] = useState<'select' | 'new'>('select');
  const [selectedLocationId, setSelectedLocationId] = useState<string | null>(null);

  // AI Credits
  const { hasCredits, consumeCredit } = useAiCredits();
  const [showPaywall, setShowPaywall] = useState(false);
  const [paywallFeature, setPaywallFeature] = useState('');

  // Drag and drop state
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  // Fetch locations from API
  const { data: savedLocations = [] } = useQuery({
    queryKey: ['locations'],
    queryFn: api.locations.list,
    enabled: isOpen,
  });

  // Fetch inventories for transfer
  const { data: inventories = [] } = useQuery({
    queryKey: ['inventories'],
    queryFn: api.inventories.list,
    enabled: isOpen,
  });

  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const locationImageInputRef = useRef<HTMLInputElement>(null);
  const thumbnailRefs = useRef<(HTMLDivElement | null)[]>([]);

  const { data: item, isLoading } = useQuery({
    queryKey: ['item', itemId],
    queryFn: () => api.items.get(itemId),
    enabled: isOpen && !!itemId,
  });

  // Initialize edit data when item loads
  useEffect(() => {
    if (item && isOpen) {
      setEditData({
        title: item.title,
        description: item.description,
        location: item.location || '',
        category: item.category as ItemCategory,
        condition: (item.condition as ItemCondition) || 'good',
      });
      setLocationImageData(null);
      setSelectedImageIndex(0);

      // Check if item's location matches a saved location
      const matchingLocation = savedLocations.find(
        (loc) => loc.name.toLowerCase() === (item.location || '').toLowerCase()
      );

      if (matchingLocation) {
        setSelectedLocationId(matchingLocation.id);
        setLocationMode('select');
      } else if (item.location) {
        setSelectedLocationId(null);
        setLocationMode('new');
      } else {
        const lastLocation = savedLocations[0];
        if (lastLocation) {
          setSelectedLocationId(lastLocation.id);
          setEditData((prev) => ({ ...prev, location: lastLocation.name }));
          setLocationMode('select');
        } else {
          setSelectedLocationId(null);
          setLocationMode('new');
        }
      }
    }
  }, [item, isOpen, savedLocations]);

  const updateMutation = useMutation({
    mutationFn: (data: UpdateItemInput) => api.items.update(itemId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
      onClose();
    },
  });

  const generateContentMutation = useMutation({
    mutationFn: () => api.items.generateContent(itemId),
    onSuccess: (data) => {
      setEditData((prev) => ({
        ...prev,
        title: data.title,
        description: data.description,
      }));
    },
  });

  const addImagesMutation = useMutation({
    mutationFn: (images: Array<{ data: string; mimeType: string }>) =>
      api.items.addImages(itemId, images),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      setIsUploadingImages(false);
    },
    onError: () => {
      setIsUploadingImages(false);
    },
  });

  const deleteImageMutation = useMutation({
    mutationFn: (imageId: string) => api.items.deleteOneImage(itemId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      if (item?.images && selectedImageIndex >= item.images.length - 1) {
        setSelectedImageIndex(Math.max(0, selectedImageIndex - 1));
      }
    },
  });

  const deleteItemMutation = useMutation({
    mutationFn: () => api.items.delete(itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      onClose();
    },
  });

  const reorderImagesMutation = useMutation({
    mutationFn: (imageIds: string[]) => api.items.reorderImages(itemId, imageIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const createLocationMutation = useMutation({
    mutationFn: ({ name, imageData }: { name: string; imageData?: { data: string; mimeType: string } }) =>
      api.locations.create(name, imageData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });

  const rotateImageMutation = useMutation({
    mutationFn: ({ imageId, direction }: { imageId: string; direction: 'left' | 'right' }) =>
      api.items.rotateImage(itemId, imageId, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const aiCropImageMutation = useMutation({
    mutationFn: (imageId: string) => api.items.aiCropImage(itemId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const undoAiCropMutation = useMutation({
    mutationFn: (imageId: string) => api.items.undoAiCrop(itemId, imageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const transferMutation = useMutation({
    mutationFn: (inventoryId: string) => api.items.transfer(itemId, inventoryId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['items'] });
      queryClient.invalidateQueries({ queryKey: ['inventories'] });
      setTransferInventoryId('');
      onClose();
    },
  });

  const handleGenerateContent = async () => {
    if (!hasCredits) {
      setPaywallFeature('AI Content Generation');
      setShowPaywall(true);
      return;
    }

    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('AI Content Generation');
      setShowPaywall(true);
      return;
    }

    generateContentMutation.mutate();
  };

  const handleAiCrop = async (imageId: string) => {
    if (!hasCredits) {
      setPaywallFeature('AI Crop');
      setShowPaywall(true);
      return;
    }

    const { success } = await consumeCredit();
    if (!success) {
      setPaywallFeature('AI Crop');
      setShowPaywall(true);
      return;
    }

    aiCropImageMutation.mutate(imageId);
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setIsUploadingImages(true);
    const images: Array<{ data: string; mimeType: string }> = [];

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) continue;

      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          const result = reader.result as string;
          resolve(result.split(',')[1] || '');
        };
        reader.readAsDataURL(file);
      });

      if (base64) {
        images.push({ data: base64, mimeType: file.type });
      }
    }

    if (images.length > 0) {
      addImagesMutation.mutate(images);
    } else {
      setIsUploadingImages(false);
    }

    e.target.value = '';
  };

  const handleDeleteImage = (imageId: string) => {
    if (confirm('Delete this photo?')) {
      deleteImageMutation.mutate(imageId);
    }
  };

  const handleDeleteItem = () => {
    if (confirm('Are you sure you want to delete this item? This action cannot be undone.')) {
      deleteItemMutation.mutate();
    }
  };

  const navigateImage = (direction: 'prev' | 'next') => {
    if (!item?.images) return;
    const newIndex =
      direction === 'prev'
        ? (selectedImageIndex - 1 + item.images.length) % item.images.length
        : (selectedImageIndex + 1) % item.images.length;
    setSelectedImageIndex(newIndex);
  };

  const handleLocationImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      if (base64) {
        setLocationImageData({
          data: base64,
          mimeType: file.type,
          preview: result,
        });
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  const handleSelectSavedLocation = (locationId: string) => {
    const location = savedLocations.find((loc) => loc.id === locationId);
    if (location) {
      setSelectedLocationId(locationId);
      setEditData((prev) => ({ ...prev, location: location.name }));
      setLocationImageData(null);
      setLocationMode('select');
    }
  };

  const handleCreateNewLocation = () => {
    setSelectedLocationId(null);
    setEditData((prev) => ({ ...prev, location: '' }));
    setLocationImageData(null);
    setLocationMode('new');
  };

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    completeReorder(dropIndex);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const completeReorder = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex || !item?.images) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newOrder = [...item.images];
    const [draggedItem] = newOrder.splice(draggedIndex, 1);
    if (draggedItem) {
      newOrder.splice(dropIndex, 0, draggedItem);
    }

    reorderImagesMutation.mutate(newOrder.map((img) => img.id));
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const moveImage = (index: number, direction: 'left' | 'right') => {
    if (!item?.images) return;
    const newIndex = direction === 'left' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= item.images.length) return;

    const newOrder = [...item.images];
    const [movedItem] = newOrder.splice(index, 1);
    if (movedItem) {
      newOrder.splice(newIndex, 0, movedItem);
    }

    if (selectedImageIndex === index) {
      setSelectedImageIndex(newIndex);
    }

    reorderImagesMutation.mutate(newOrder.map((img) => img.id));
  };

  const saveChanges = async () => {
    const updatePayload: any = { ...editData };
    if (locationImageData) {
      updatePayload.locationImageData = {
        data: locationImageData.data,
        mimeType: locationImageData.mimeType,
      };
    }

    if (locationMode === 'new' && editData.location && editData.location.trim()) {
      try {
        await createLocationMutation.mutateAsync({
          name: editData.location.trim(),
          imageData: locationImageData
            ? {
                data: locationImageData.data,
                mimeType: locationImageData.mimeType,
              }
            : undefined,
        });
      } catch (error) {
        console.log('Location save:', error);
      }
    }

    updateMutation.mutate(updatePayload);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70" onClick={onClose} />

      {/* Modal */}
      <div className="relative min-h-screen flex items-start justify-center p-4 pt-8">
        <div className="relative bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
            <h2 className="text-lg font-semibold">Edit Item</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {isLoading ? (
              <div className="p-8 text-center text-gray-500">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                Loading item...
              </div>
            ) : !item ? (
              <div className="p-8 text-center text-gray-500">Item not found</div>
            ) : (
              <>
                {/* Image Gallery */}
                <div className="relative">
                  {/* Main Image */}
                  <div className="aspect-video bg-gray-100 flex items-center justify-center relative">
                    {item.images && item.images.length > 0 ? (
                      <>
                        <img
                          src={item.images[selectedImageIndex]?.url}
                          alt={`${item.title} - Photo ${selectedImageIndex + 1}`}
                          className="w-full h-full object-contain cursor-pointer"
                          onClick={() => setShowImageLightbox(true)}
                        />
                        {/* Navigation arrows */}
                        {item.images.length > 1 && (
                          <>
                            <button
                              onClick={() => navigateImage('prev')}
                              className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                            >
                              <ChevronLeft className="w-6 h-6" />
                            </button>
                            <button
                              onClick={() => navigateImage('next')}
                              className="absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                            >
                              <ChevronRight className="w-6 h-6" />
                            </button>
                          </>
                        )}
                        {/* Image counter */}
                        {item.images.length > 1 && (
                          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-sm px-2 py-1 rounded">
                            {selectedImageIndex + 1} / {item.images.length}
                          </div>
                        )}
                        {/* Rotate left button */}
                        <div className="absolute top-2 left-2">
                          <button
                            onClick={() =>
                              item.images[selectedImageIndex]?.id &&
                              rotateImageMutation.mutate({
                                imageId: item.images[selectedImageIndex].id,
                                direction: 'left',
                              })
                            }
                            disabled={rotateImageMutation.isPending}
                            className="w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                            title="Rotate left 90deg"
                          >
                            <RotateCcw
                              className={clsx('w-5 h-5', rotateImageMutation.isPending && 'animate-spin')}
                            />
                          </button>
                        </div>
                        {/* Image action buttons */}
                        <div className="absolute top-2 right-2 flex gap-2">
                          <button
                            onClick={() =>
                              item.images[selectedImageIndex]?.id &&
                              rotateImageMutation.mutate({
                                imageId: item.images[selectedImageIndex].id,
                                direction: 'right',
                              })
                            }
                            disabled={rotateImageMutation.isPending}
                            className="w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center"
                            title="Rotate right 90deg"
                          >
                            <RotateCw
                              className={clsx('w-5 h-5', rotateImageMutation.isPending && 'animate-spin')}
                            />
                          </button>
                          {/* AI Crop / Undo Crop button */}
                          {item.images[selectedImageIndex]?.isAiCropped ? (
                            <button
                              onClick={() =>
                                item.images[selectedImageIndex]?.id &&
                                undoAiCropMutation.mutate(item.images[selectedImageIndex].id)
                              }
                              disabled={undoAiCropMutation.isPending}
                              className="h-11 px-3 bg-amber-500 hover:bg-amber-600 text-white rounded-full flex items-center justify-center gap-1.5 text-sm font-medium"
                              title="Undo AI crop"
                            >
                              {undoAiCropMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Undo2 className="w-4 h-4" />
                              )}
                              Undo Crop
                            </button>
                          ) : (
                            <button
                              onClick={() =>
                                item.images[selectedImageIndex]?.id &&
                                handleAiCrop(item.images[selectedImageIndex].id)
                              }
                              disabled={aiCropImageMutation.isPending}
                              className="h-11 px-3 bg-purple-500 hover:bg-purple-600 text-white rounded-full flex items-center justify-center gap-1.5 text-sm font-medium"
                              title="AI crop to focus on main subject"
                            >
                              {aiCropImageMutation.isPending ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Crop className="w-4 h-4" />
                              )}
                              AI Crop
                            </button>
                          )}
                          <button
                            onClick={() =>
                              item.images[selectedImageIndex]?.id &&
                              handleDeleteImage(item.images[selectedImageIndex].id)
                            }
                            disabled={deleteImageMutation.isPending}
                            className="w-11 h-11 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center"
                            title="Delete this photo"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center">
                        <span className="text-6xl">📦</span>
                        <p className="mt-2 text-gray-500">No photos</p>
                      </div>
                    )}
                  </div>

                  {/* Thumbnails */}
                  {item.images && item.images.length > 1 && (
                    <div className="bg-gray-50 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-gray-500">
                          {item.images.length} photos - drag to reorder
                        </span>
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {item.images.map((img, index) => (
                          <div
                            key={img.id}
                            ref={(el) => {
                              thumbnailRefs.current[index] = el;
                            }}
                            className={clsx(
                              'flex-shrink-0 relative transition-transform',
                              draggedIndex === index && 'opacity-50 scale-95',
                              dragOverIndex === index && draggedIndex !== index && 'scale-105'
                            )}
                            draggable
                            onDragStart={(e) => handleDragStart(e, index)}
                            onDragOver={(e) => handleDragOver(e, index)}
                            onDrop={(e) => handleDrop(e, index)}
                            onDragEnd={handleDragEnd}
                          >
                            {index === 0 && (
                              <span className="absolute -top-1 left-1/2 -translate-x-1/2 bg-primary-500 text-white text-[10px] px-1.5 py-0.5 rounded z-10 pointer-events-none">
                                Thumbnail
                              </span>
                            )}
                            <div className="flex flex-col items-center gap-1">
                              <button
                                onClick={() => setSelectedImageIndex(index)}
                                className={clsx(
                                  'w-16 h-16 rounded-lg overflow-hidden border-2 transition-colors',
                                  selectedImageIndex === index
                                    ? 'border-primary-500'
                                    : 'border-transparent hover:border-gray-300'
                                )}
                              >
                                <img
                                  src={img.thumbnailUrl || img.url}
                                  alt={`Thumbnail ${index + 1}`}
                                  className="w-full h-full object-cover pointer-events-none"
                                  draggable={false}
                                />
                              </button>
                              <div className="flex gap-1">
                                <button
                                  onClick={() => moveImage(index, 'left')}
                                  disabled={index === 0}
                                  className={clsx(
                                    'w-6 h-6 flex items-center justify-center rounded text-xs',
                                    index === 0 ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-200'
                                  )}
                                >
                                  ←
                                </button>
                                <button
                                  onClick={() => moveImage(index, 'right')}
                                  disabled={index === item.images.length - 1}
                                  className={clsx(
                                    'w-6 h-6 flex items-center justify-center rounded text-xs',
                                    index === item.images.length - 1
                                      ? 'text-gray-300'
                                      : 'text-gray-500 hover:bg-gray-200'
                                  )}
                                >
                                  →
                                </button>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Add photos buttons */}
                  <div className="flex gap-2 p-3 border-t">
                    <input
                      id="edit-modal-camera-input"
                      ref={cameraInputRef}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={handleFileSelect}
                      className="sr-only"
                      disabled={isUploadingImages}
                    />
                    <input
                      id="edit-modal-file-input"
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="sr-only"
                      disabled={isUploadingImages}
                    />
                    <label
                      htmlFor="edit-modal-camera-input"
                      className={clsx(
                        'btn-secondary flex-1 text-sm flex items-center justify-center cursor-pointer',
                        isUploadingImages && 'opacity-50 pointer-events-none'
                      )}
                    >
                      <Camera className="w-4 h-4 mr-1" />
                      Take Photo
                    </label>
                    <label
                      htmlFor="edit-modal-file-input"
                      className={clsx(
                        'btn-secondary flex-1 text-sm flex items-center justify-center cursor-pointer',
                        isUploadingImages && 'opacity-50 pointer-events-none'
                      )}
                    >
                      <ImagePlus className="w-4 h-4 mr-1" />
                      {isUploadingImages ? 'Uploading...' : 'Add Photos'}
                    </label>
                  </div>

                  {/* GPS Map */}
                  {item.images &&
                    item.images[selectedImageIndex]?.latitude &&
                    item.images[selectedImageIndex]?.longitude && (
                      <div className="p-3 border-t">
                        <GpsMap
                          latitude={item.images[selectedImageIndex].latitude!}
                          longitude={item.images[selectedImageIndex].longitude!}
                          title={item.title}
                        />
                      </div>
                    )}
                </div>

                {/* Form Fields */}
                <div className="p-6 space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                    <input
                      type="text"
                      value={editData.title || ''}
                      onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                      className="input"
                      spellCheck={true}
                      autoCorrect="on"
                      autoCapitalize="sentences"
                      autoComplete="one-time-code"
                    />
                  </div>

                  {/* AI Content Generation Button */}
                  {item.images && item.images.length > 0 && (
                    <button
                      type="button"
                      onClick={handleGenerateContent}
                      disabled={generateContentMutation.isPending}
                      className={clsx(
                        'w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border-2 border-dashed transition-colors',
                        generateContentMutation.isPending
                          ? 'bg-purple-50 border-purple-300 text-purple-600 cursor-wait'
                          : 'bg-white border-purple-200 text-purple-600 hover:bg-purple-50 hover:border-purple-400'
                      )}
                    >
                      {generateContentMutation.isPending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Analyzing images...
                        </>
                      ) : (
                        <>
                          <Sparkles className="w-4 h-4" />
                          Generate with AI
                        </>
                      )}
                    </button>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                    <select
                      value={editData.category || ''}
                      onChange={(e) =>
                        setEditData({ ...editData, category: e.target.value as ItemCategory })
                      }
                      className="input"
                    >
                      {ITEM_CATEGORIES.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Condition</label>
                    <select
                      value={editData.condition || 'good'}
                      onChange={(e) =>
                        setEditData({ ...editData, condition: e.target.value as ItemCondition })
                      }
                      className="input"
                    >
                      {ITEM_CONDITIONS.map((cond) => (
                        <option key={cond} value={cond}>
                          {ITEM_CONDITION_LABELS[cond]}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <PhraseDropdown
                        onInsert={(phrase) => {
                          const currentDesc = editData.description || '';
                          const separator = currentDesc && !currentDesc.endsWith('\n') && !currentDesc.endsWith(' ') ? '\n' : '';
                          setEditData({ ...editData, description: currentDesc + separator + phrase });
                        }}
                      />
                    </div>
                    <textarea
                      value={editData.description || ''}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={4}
                      className="input"
                      spellCheck={true}
                      autoCorrect="on"
                      autoCapitalize="sentences"
                      autoComplete="one-time-code"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>

                    <select
                      value={locationMode === 'new' ? '__new__' : selectedLocationId || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === '__new__') {
                          handleCreateNewLocation();
                        } else if (value === '') {
                          setSelectedLocationId(null);
                          setEditData((prev) => ({ ...prev, location: '' }));
                          setLocationImageData(null);
                          setLocationMode('select');
                        } else {
                          handleSelectSavedLocation(value);
                        }
                      }}
                      className="input"
                    >
                      <option value="">No location</option>
                      {savedLocations.map((loc) => (
                        <option key={loc.id} value={loc.id}>
                          {loc.name}
                        </option>
                      ))}
                      <option value="__new__">+ Add new location...</option>
                    </select>

                    {/* Selected location preview */}
                    {locationMode === 'select' &&
                      selectedLocationId &&
                      (() => {
                        const selectedLocation = savedLocations.find(
                          (loc) => loc.id === selectedLocationId
                        );
                        const locationImage = selectedLocation?.imageUrl || item?.locationImage;
                        return (
                          <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                            <div className="flex items-center gap-3">
                              {locationImage ? (
                                <img
                                  src={locationImage}
                                  alt="Location"
                                  className="w-12 h-12 rounded-lg object-cover"
                                />
                              ) : (
                                <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
                                  <MapPin className="w-6 h-6 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">
                                  {editData.location}
                                </p>
                                <p className="text-xs text-gray-500">Saved location</p>
                              </div>
                            </div>
                          </div>
                        );
                      })()}

                    {/* New location input */}
                    {locationMode === 'new' && (
                      <div className="mt-3 space-y-3">
                        <input
                          type="text"
                          value={editData.location || ''}
                          onChange={(e) => setEditData({ ...editData, location: e.target.value })}
                          placeholder="e.g. Top drawer of pantry"
                          className="input"
                          spellCheck={true}
                          autoCorrect="on"
                          autoCapitalize="sentences"
                      autoComplete="one-time-code"
                        />

                        <input
                          id="edit-modal-location-image-input"
                          ref={locationImageInputRef}
                          type="file"
                          accept="image/*"
                          capture="environment"
                          onChange={handleLocationImageSelect}
                          className="sr-only"
                        />
                        {locationImageData ? (
                          <div className="flex items-center gap-3">
                            <div className="relative w-16 h-16 flex-shrink-0">
                              <img
                                src={locationImageData.preview}
                                alt="Location"
                                className="w-full h-full object-cover rounded-lg"
                              />
                              <button
                                onClick={() => setLocationImageData(null)}
                                className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-md"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                            <span className="text-sm text-gray-600">New location photo</span>
                          </div>
                        ) : item?.locationImage ? (
                          <div className="flex items-center gap-3">
                            <img
                              src={item.locationImage}
                              alt="Location"
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                            <div className="flex flex-col gap-1">
                              <span className="text-sm text-gray-600">Current location photo</span>
                              <label
                                htmlFor="edit-modal-location-image-input"
                                className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                              >
                                <Camera className="w-4 h-4" />
                                Change photo
                              </label>
                            </div>
                          </div>
                        ) : (
                          <label
                            htmlFor="edit-modal-location-image-input"
                            className="text-sm text-primary-600 hover:text-primary-700 flex items-center gap-1 cursor-pointer"
                          >
                            <Camera className="w-4 h-4" />
                            Add photo of location
                          </label>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Transfer to another inventory */}
                  {inventories.length > 1 && item && (
                    <div className="pt-4 border-t">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Transfer to another inventory
                      </label>
                      <div className="flex gap-2">
                        <select
                          value={transferInventoryId}
                          onChange={(e) => setTransferInventoryId(e.target.value)}
                          className="input flex-1"
                        >
                          <option value="">Select inventory...</option>
                          {inventories
                            .filter((inv) => inv.id !== item.inventoryId)
                            .map((inv) => (
                              <option key={inv.id} value={inv.id}>
                                {inv.name}
                              </option>
                            ))}
                        </select>
                        <button
                          onClick={() => transferInventoryId && transferMutation.mutate(transferInventoryId)}
                          disabled={!transferInventoryId || transferMutation.isPending}
                          className="btn-secondary"
                        >
                          {transferMutation.isPending ? 'Transferring...' : 'Transfer'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          {item && (
            <div className="flex gap-2 p-4 border-t sticky bottom-0 bg-white">
              <button type="button" onClick={onClose} className="btn-secondary flex-1">
                Cancel
              </button>
              <button
                onClick={saveChanges}
                disabled={updateMutation.isPending}
                className="btn-primary flex-1"
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-1" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-1" />
                    Save Changes
                  </>
                )}
              </button>
              <button
                onClick={handleDeleteItem}
                disabled={deleteItemMutation.isPending}
                className="btn-danger"
              >
                {deleteItemMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Image Lightbox */}
      {showImageLightbox && item?.images && item.images.length > 0 && (
        <ImageLightbox
          images={item.images}
          initialIndex={selectedImageIndex}
          onClose={() => setShowImageLightbox(false)}
          title={item.title}
        />
      )}

      {/* AI Credits Paywall Modal */}
      <PaywallModal
        isOpen={showPaywall}
        onClose={() => setShowPaywall(false)}
        featureName={paywallFeature}
      />
    </div>
  );
}
